import { createActions, createReducer } from "reduxsauce";
import { updateStateGeneric } from "@redux-core/utils";
import { cloneDeep, forEach, isEmpty, set } from "@utils/lodash";

/* ------------- Initial State ------------- */

const INITIAL_STATE = {
	image: {
		profilePicUrl: "",
	},
	agency: {},
	contacts: [],
	jurisdictionFees: {
		general: {
			data: [],
			validations: [],
		},
		vehicle: {
			data: [],
			validations: [],
		},
		asset: {
			data: [],
			validations: [],
		},
	},
	agencyRestrictions: {
		required: {
			data: [],
			validations: [],
		},
		excluded: {
			data: [],
			validations: [],
		},
	},
	jurisdictionPaymentRules: {
		TransferPaymentToJurisdiction: false,
		IsOfficerInvoiceApprovalRequired: false,
	},
	jurisdictionAgencyRules: {
		OfficerInsuranceOptIn: false,
		OfficerCoordinatedJobsVisible: false,
		IsJobLocationApprovalRequired: false,
	},
	jurisdictionOfficerRules: {
		_limitedHours: false,
		MaxWeeklyHours: 0,
		MaxWeeklyHoursType: 1,
		ExtraDutyApprovalRequired: false,
		SkipOfficerReVerification: false,
	},
	loaders: {
		contacts: false,
		jurisdictionFees: false,
		jurisdictionPaymentRules: false,
		jurisdictionAgencyRules: false,
		agencyRestrictions: false,
		data: false,
		image: false,
		saveButton: "",
	},
};

/* ------------- Types and Action Creators ------------- */
const { Types, Creators } = createActions(
	{
		receiveData: ["collection"],
		receiveAgency: ["value"],
		updateAgency: ["property", "value"],
		setProfilePic: ["profilePicUrl"],
		updateLoaders: ["property", "value"],
		getContacts: ["contacts"],
		receiveJurisdictionFees: ["data"],
		updateJurisdictionFees: ["property", "data"],
		resetJurisdictionFees: null,
		receiveAgencyRestrictions: ["data"],
		updateAgencyRestrictions: ["property", "data"],
		resetAgencyRestrictions: null,
	},
	{ prefix: "@account/" },
);

/* ------------- Reducers ------------- */

export const reducer = createReducer(INITIAL_STATE, {
	[Types.RECEIVE_DATA]: (state, { collection }) => {
		if (!isEmpty(collection)) {
			const newState = cloneDeep(state);

			forEach(collection, ({ property, value }) => {
				set(newState, property, value);
			});

			return newState;
		}
		return state;
	},

	[Types.RECEIVE_AGENCY]: (state, { value }) =>
		updateStateGeneric({ state, property: "agency", value }),

	[Types.UPDATE_AGENCY]: (state, { property, value }) =>
		updateStateGeneric({ state, property: `agency.${property}`, value }),

	[Types.SET_PROFILE_PIC]: (state, { profilePicUrl }) => {
		const newState = cloneDeep(state);
		set(newState, ["image", "profilePicUrl"], profilePicUrl);
		return newState;
	},

	[Types.UPDATE_LOADERS]: (state, { property, value }) =>
		updateStateGeneric({ state, property: `loaders.${property}`, value }),

	[Types.GET_CONTACTS]: (state, { contacts }) => {
		const newState = cloneDeep(state);
		set(newState, ["contacts"], contacts);
		return newState;
	},

	[Types.RECEIVE_JURISDICTION_FEES]: (state, { data }) => {
		const newState = cloneDeep(state);
		set(newState, ["jurisdictionFees", "general", "data"], data.general);
		set(newState, ["jurisdictionFees", "vehicle", "data"], data.vehicle);
		set(newState, ["jurisdictionFees", "asset", "data"], data.asset);

		return newState;
	},

	[Types.UPDATE_JURISDICTION_FEES]: (state, { property, data }) => {
		const newState = cloneDeep(state);
		set(newState, `jurisdictionFees.${property}`, data);

		return newState;
	},

	[Types.RESET_JURISDICTION_FEES]: state => ({
		...state,
		jurisdictionFees: INITIAL_STATE.jurisdictionFees,
	}),

	[Types.RECEIVE_AGENCY_RESTRICTIONS]: (state, { data }) => {
		const newState = cloneDeep(state);
		set(newState, ["agencyRestrictions", "required", "data"], data.required);
		set(newState, ["agencyRestrictions", "excluded", "data"], data.excluded);

		return newState;
	},

	[Types.UPDATE_AGENCY_RESTRICTIONS]: (state, { property, data }) => {
		const newState = cloneDeep(state);
		set(newState, `agencyRestrictions.${property}`, data);

		return newState;
	},

	[Types.RESET_AGENCY_RESTRICTIONS]: state => ({
		...state,
		agencyRestrictions: INITIAL_STATE.agencyRestrictions,
	}),
});

export default Creators;
